/** Rows for account create. Empty password means the screen fills it. */
export interface ImportedAccount {
  login: string;
  first: string;
  last: string;
  email: string;
  password: string;
  group: string;
}

export interface ImportIssue {
  row: number;
  code: 'login' | 'duplicate';
  login?: string;
}

export const MAX_IMPORT = 500;

type Col = 'login' | 'first' | 'last' | 'name' | 'email' | 'password' | 'group';
type HeaderCol = Col | 'given';

const ALIAS: Record<string, HeaderCol> = {
  login: 'login',
  username: 'login',
  user: 'login',
  uid: 'login',
  samaccountname: 'login',
  uzivatel: 'login',
  'prihlasovaci jmeno': 'login',
  'first name': 'first',
  firstname: 'first',
  givenname: 'first',
  'given name': 'first',
  krestni: 'first',
  'krestni jmeno': 'first',
  'last name': 'last',
  lastname: 'last',
  surname: 'last',
  sn: 'last',
  prijmeni: 'last',
  name: 'name',
  'display name': 'name',
  displayname: 'name',
  cn: 'name',
  'cele jmeno': 'name',
  jmeno: 'given',
  email: 'email',
  'e mail': 'email',
  mail: 'email',
  password: 'password',
  passwd: 'password',
  pwd: 'password',
  heslo: 'password',
  group: 'group',
  groups: 'group',
  skupina: 'group',
};

function fold(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[_./-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function splitName(full: string): { first: string; last: string } {
  const parts = full.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: '', last: '' };
  if (parts.length === 1) return { first: parts[0]!, last: '' };
  return { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1]! };
}

function countUnquoted(line: string, sep: string): number {
  let count = 0;
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (quoted && line[i + 1] === '"') i += 1;
      else quoted = !quoted;
    } else if (!quoted && ch === sep) count += 1;
  }
  return count;
}

function detectSep(line: string): string {
  const tab = countUnquoted(line, '\t');
  const semi = countUnquoted(line, ';');
  const comma = countUnquoted(line, ',');
  if (tab >= semi && tab >= comma && tab > 0) return '\t';
  if (semi >= comma && semi > 0) return ';';
  return ',';
}

function splitCsv(line: string, sep: string): string[] {
  const out: string[] = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]!;
    if (quoted) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else quoted = false;
      } else cur += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === sep) {
      out.push(cur.trim());
      cur = '';
    } else cur += ch;
  }
  out.push(cur.trim());
  return out;
}

function guess(cells: string[]): Col[] {
  if (cells.length <= 1) return ['login'];
  if (cells.length === 2) return cells[1]?.includes('@') ? ['login', 'email'] : ['login', 'name'];
  if (cells.length === 3) {
    if (cells[2]?.includes('@')) return ['login', 'name', 'email'];
    if (cells[1]?.includes('@')) return ['login', 'email', 'name'];
    return ['login', 'first', 'last'];
  }
  const base: Col[] = ['login', 'first', 'last', 'email', 'password', 'group'];
  return base.slice(0, Math.min(cells.length, base.length));
}

function accountFrom(cells: string[], columns: Array<Col | null>): ImportedAccount {
  const raw: Partial<Record<Col, string>> = {};
  columns.forEach((col, index) => {
    if (col) raw[col] = (cells[index] ?? '').trim();
  });
  let first = raw.first || '';
  let last = raw.last || '';
  if (!first || !last) {
    const named = splitName(raw.name || '');
    if (!first) first = named.first;
    if (!last) last = named.last;
  }
  return {
    login: (raw.login || '').trim(),
    first,
    last,
    email: (raw.email || '').trim(),
    password: raw.password || '',
    group: (raw.group || '').trim(),
  };
}

export function parseAccountTable(text: string): {
  accounts: ImportedAccount[];
  issues: ImportIssue[];
  limited: boolean;
} {
  const rawLines = text.replace(/^\uFEFF/, '').split(/\r?\n/);
  const lines = rawLines
    .map((line, index) => ({ line: line.trim(), row: index + 1 }))
    .filter((item) => item.line && !item.line.startsWith('#') && !/^sep\s*=/i.test(item.line));
  if (lines.length === 0) return { accounts: [], issues: [], limited: false };

  const sep = detectSep(lines[0]!.line);
  const table = lines.map((item) => ({ cells: splitCsv(item.line, sep), row: item.row }));
  const header = table[0]!.cells.map((cell) => ALIAS[fold(cell)] ?? null);
  const hasHeader = header.some(Boolean);
  const hasLast = header.includes('last');
  const columns: Array<Col | null> = hasHeader
    ? header.map((col) => (col === 'given' ? (hasLast ? 'first' : 'name') : col))
    : guess(table[0]!.cells);
  const body = hasHeader ? table.slice(1) : table;
  const limited = body.length > MAX_IMPORT;
  if (limited) return { accounts: [], issues: [], limited: true };

  const accounts: ImportedAccount[] = [];
  const issues: ImportIssue[] = [];
  const seen = new Set<string>();
  for (const item of body) {
    if (item.cells.every((cell) => !cell.trim())) continue;
    const account = accountFrom(item.cells, columns);
    if (!account.login) {
      issues.push({ row: item.row, code: 'login' });
      continue;
    }
    const key = account.login.toLowerCase();
    if (seen.has(key)) {
      issues.push({ row: item.row, code: 'duplicate', login: account.login });
      continue;
    }
    seen.add(key);
    accounts.push(account);
  }
  return { accounts, issues, limited: false };
}
